import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";

import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import FilterDropdown from "designV2/components/FilterDropdown";
import StatusBadge from "designV2/components/StatusBadge";
import { endPoint } from "utils/config";
import { assetUrl, readableError, humanize } from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

const EDIT_DRIVER = gql`
  mutation updateDriverInfoByAdmin(
    $driverId: ID!
    $gender: DriverGender
    $address: DriverAddressInput
    $birthDate: Date
    $profileImageUrl: String
    $fullName: String
    $emailVerified: Boolean
    $phoneNumberVerified: Boolean
    $hasNotification: Boolean
    $email: String
    $phoneNumber: String
  ) {
    updateDriverInfoByAdmin(
      driverId: $driverId
      input: {
        gender: $gender
        address: $address
        emailVerified: $emailVerified
        hasNotification: $hasNotification
        phoneNumberVerified: $phoneNumberVerified
        birthDate: $birthDate
        profileImageUrl: $profileImageUrl
        fullName: $fullName
        email: $email
        phoneNumber: $phoneNumber
      }
    ) {
      _id
      fullName
    }
  }
`;

const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];
const GENDER_LABELS = GENDER_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const toInputDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

const Toggle = ({ label, hint, checked, onChange }) => (
  <label className={`dsOptionCard ${checked ? "isSelected" : ""}`}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="dsOptionCardText">
      <strong>{label}</strong>
      <span>{hint}</span>
    </span>
  </label>
);

/**
 * EditDriverForm - edit one driver's own details.
 *
 * Vehicles, the driving licence and the verification decision are not
 * edited here: those live on the vehicle screen and the verification
 * queue respectively, which is where the approve/reject flow already is.
 */
const EditDriverForm = ({ driver }) => {
  const [fullName, setFullName] = useState(driver.fullName || "");
  const [email, setEmail] = useState(driver.email || "");
  const [phoneNumber, setPhoneNumber] = useState(driver.phoneNumber || "");
  const [gender, setGender] = useState(driver.gender || "");
  const [birthDate, setBirthDate] = useState(toInputDate(driver.birthDate));
  const [addressFull, setAddressFull] = useState(
    driver.address && driver.address.full ? driver.address.full : ""
  );
  const [addressZip, setAddressZip] = useState(
    driver.address && driver.address.zipCode ? driver.address.zipCode : ""
  );
  const [profileImageUrl, setProfileImageUrl] = useState(driver.profileImageUrl || "");
  const [emailVerified, setEmailVerified] = useState(Boolean(driver.emailVerified));
  const [phoneNumberVerified, setPhoneNumberVerified] = useState(
    Boolean(driver.phoneNumberVerified)
  );
  const [hasNotification, setHasNotification] = useState(Boolean(driver.hasNotification));

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [updateDriver] = useMutation(EDIT_DRIVER);

  const onPickPhoto = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setUploading(true);
    setFormError("");
    uploadFile({ variables: { file, folderName: "drivers" } })
      .then((res) => {
        setUploading(false);
        setProfileImageUrl(res.data.uploadFile.url);
      })
      .catch((err) => {
        setUploading(false);
        setFormError(readableError(err));
      });
    event.target.value = "";
  };

  const validate = () => {
    const next = {};
    if (!fullName.trim()) next.fullName = "A name is required.";
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      next.email = "That does not look like an email.";
    }
    if (!phoneNumber.trim()) next.phoneNumber = "A phone number is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving || uploading) return;
    setFormError("");
    if (!validate()) return;

    setSaving(true);
    updateDriver({
      variables: {
        driverId: driver._id,
        fullName: fullName.trim(),
        email: email.trim() || undefined,
        phoneNumber: phoneNumber.trim(),
        gender: gender || undefined,
        birthDate: birthDate || undefined,
        profileImageUrl: profileImageUrl || undefined,
        // The address input takes both halves together, so an empty
        // form must send nothing rather than a half-filled object.
        address:
          addressFull.trim() || addressZip.trim()
            ? { full: addressFull.trim(), zipCode: addressZip.trim() }
            : undefined,
        emailVerified,
        phoneNumberVerified,
        hasNotification,
      },
    })
      .then(() => {
        window.location.href = `${endPoint}/superAdmin/drivers`;
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  const preview = assetUrl(profileImageUrl);

  return (
    <div className="page superPeoplePage">
      <main className="main">
        <PageHeader
          title="Edit driver"
          description={driver.fullName || ""}
          actions={
            <StatusBadge
              status={driver.state === "SUSPENDED" ? "danger" : "success"}
              label={humanize(driver.state) || "Unknown"}
            />
          }
        />

        <form onSubmit={onSubmit}>
          <Panel title="Profile photo">
            <div className="superPersonImages">
              {preview && (
                <span className="superPersonImageButton">
                  <img src={preview} alt="Driver" />
                </span>
              )}
              <label className="superPersonFilePicker">
                <input type="file" accept="image/*" hidden onChange={onPickPhoto} />
                {uploading ? "Uploading…" : preview ? "Replace photo" : "Choose a photo"}
              </label>
            </div>
          </Panel>

          <Panel title="Details">
            <div className="superPersonForm">
              {formError && (
                <div className="dsFormMessage error" role="alert">
                  {formError}
                </div>
              )}

              <Field label="Full name" htmlFor="fullName" error={errors.fullName} required>
                <input
                  id="fullName"
                  className="dsInput"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Field>

              <div className="superPersonFormRow">
                <Field label="Email" htmlFor="email" error={errors.email} optional>
                  <input
                    id="email"
                    className="dsInput"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>

                <Field label="Phone number" htmlFor="phoneNumber" error={errors.phoneNumber} required>
                  <input
                    id="phoneNumber"
                    className="dsInput"
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </Field>
              </div>

              <div className="superPersonFormRow">
                <Field label="Date of birth" htmlFor="birthDate" optional>
                  <input
                    id="birthDate"
                    className="dsInput"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </Field>

                <Field label="Gender" optional>
                  <FilterDropdown
                    label={gender ? GENDER_LABELS[gender] : "Not set"}
                    options={[{ value: "", label: "Not set" }, ...GENDER_OPTIONS]}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  />
                </Field>
              </div>

              <div className="superPersonFormRow">
                <Field label="Address" htmlFor="addressFull" optional>
                  <input
                    id="addressFull"
                    className="dsInput"
                    type="text"
                    value={addressFull}
                    onChange={(e) => setAddressFull(e.target.value)}
                  />
                </Field>

                <Field label="Postal code" htmlFor="addressZip" optional>
                  <input
                    id="addressZip"
                    className="dsInput"
                    type="text"
                    value={addressZip}
                    onChange={(e) => setAddressZip(e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </Panel>

          <Panel
            title="Overrides"
            subtitle="These skip the confirmation the driver would otherwise have to complete."
          >
            <div className="superPersonForm">
              <Toggle
                label="Phone verified"
                hint="Lets the driver sign in without confirming their number."
                checked={phoneNumberVerified}
                onChange={() => setPhoneNumberVerified((current) => !current)}
              />
              <Toggle
                label="Email verified"
                hint="Skips the confirmation email."
                checked={emailVerified}
                onChange={() => setEmailVerified((current) => !current)}
              />
              <Toggle
                label="Push notifications"
                hint="Whether the driver receives trip alerts on their phone."
                checked={hasNotification}
                onChange={() => setHasNotification((current) => !current)}
              />
            </div>
          </Panel>

          <div className="superPersonFooter">
            <Button
              variant="secondary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/drivers`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving} disabled={uploading}>
              Save changes
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default EditDriverForm;
